import {
  formatHistoryActorLabel,
  formatThaiDatetime,
} from "@aera/data-table";
import type { ReceivableListItem } from "@/lib/pendingOrders";
import type { ReceivableHistoryEntry } from "@/app/artifacts/table/receivableHistory";
import { resolveReceivableDisplayStatus } from "@/lib/receivableDisplayStatus";

export type ReceivableStaleNotice = {
  actorName: string;
  actorId?: string;
  summary: string;
  occurredAt: string;
  actionTitle?: string;
  /** Latest history description, e.g. "แก้ยอดค้าง 1500 → 2000 บาท" */
  changeDescription?: string;
};

export type ReceivableStaleFieldDiff = {
  label: string;
  localValue: string;
  remoteValue: string;
};

export type ReceivableStaleConflict = {
  notice: ReceivableStaleNotice;
  localItem: ReceivableListItem;
  remoteItem: ReceivableListItem;
  diffs: ReceivableStaleFieldDiff[];
};

/** Freeze list-item values so later list refreshes cannot mutate the open-modal snapshot. */
export function cloneReceivableListItem(
  item: ReceivableListItem,
): ReceivableListItem {
  return {
    ...item,
    statuses: [...(item.statuses ?? [])],
    fields: (item.fields ?? []).map((field) => ({ ...field })),
  };
}

function pickField(item: ReceivableListItem, key: string): string {
  return item.fields.find((field) => field.key === key)?.value?.trim() ?? "";
}

function parseAmountNumber(raw: string): number | null {
  const normalized = raw.replace(/[^\d.,-]/g, "").replace(/,/g, "");
  if (!normalized) return null;
  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) ? value : null;
}

function formatBahtDisplay(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "—") return "—";
  const value = parseAmountNumber(trimmed);
  if (value === null) return trimmed;
  return `฿${value.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function pickAmountOriginal(item: ReceivableListItem): string {
  return (
    pickField(item, "amount_original") ||
    pickField(item, "slip_amount_original") ||
    pickField(item, "slip_amount") ||
    ""
  );
}

function pickAmountRemaining(item: ReceivableListItem): string {
  return (
    pickField(item, "amount_remaining") ||
    pickField(item, "amount") ||
    item.amountLabel?.trim() ||
    ""
  );
}

function pickAmountPaid(item: ReceivableListItem): string {
  return pickField(item, "amount_paid") || "";
}

function pickStatuses(item: ReceivableListItem): string {
  return resolveReceivableDisplayStatus(item);
}

function amountsEqual(a: string, b: string): boolean {
  const na = parseAmountNumber(a);
  const nb = parseAmountNumber(b);
  if (na !== null && nb !== null) return Math.abs(na - nb) < 0.005;
  return a.trim() === b.trim();
}

function pushAmountDiff(
  diffs: ReceivableStaleFieldDiff[],
  label: string,
  localRaw: string,
  remoteRaw: string,
) {
  if (!localRaw && !remoteRaw) return;
  if (amountsEqual(localRaw || "—", remoteRaw || "—")) return;
  if (diffs.some((d) => d.label === label)) return;
  diffs.push({
    label,
    localValue: formatBahtDisplay(localRaw || "—"),
    remoteValue: formatBahtDisplay(remoteRaw || "—"),
  });
}

function pushTextDiff(
  diffs: ReceivableStaleFieldDiff[],
  label: string,
  localRaw: string,
  remoteRaw: string,
) {
  const local = localRaw.trim() || "—";
  const remote = remoteRaw.trim() || "—";
  if (local === remote) return;
  if (diffs.some((d) => d.label === label)) return;
  diffs.push({ label, localValue: local, remoteValue: remote });
}

function pickDisplayName(item: ReceivableListItem): string {
  return (
    pickField(item, "customer_ref") ||
    pickField(item, "customer_name") ||
    pickField(item, "slip_sender") ||
    pickField(item, "slip_sender_name") ||
    pickField(item, "sender_name") ||
    pickField(item, "addr_name") ||
    pickField(item, "recipient_name") ||
    ""
  );
}

function pickSlipSender(item: ReceivableListItem): string {
  return (
    pickField(item, "slip_sender") ||
    pickField(item, "slip_sender_name") ||
    pickField(item, "sender_name") ||
    ""
  );
}

function pickAddressText(item: ReceivableListItem): string {
  return (
    pickField(item, "address_text") ||
    pickField(item, "address") ||
    pickField(item, "full_address") ||
    ""
  );
}

const FIELD_DIFF_SKIP_KEYS = new Set([
  "updated_at",
  "updated_by",
  "updated_by_name",
  "last_actor_name",
  "revision",
  "expires_at",
  "age_label",
  "source_channel",
  "image_url",
  "slip_image_url",
  // Structured picks already cover these — avoid duplicate synonym rows.
  "amount",
  "amount_original",
  "amount_remaining",
  "amount_paid",
  "slip_amount",
  "slip_amount_original",
  "customer_ref",
  "customer_name",
  "slip_sender",
  "slip_sender_name",
  "sender_name",
  "addr_name",
  "recipient_name",
  "address_text",
  "address",
  "full_address",
]);

/** Preferred label first within each synonym family (same before → after). */
const DIFF_LABEL_PRIORITY: string[] = [
  "ชื่อ",
  "ผู้โอน",
  "ที่อยู่",
  "ยอดเต็ม",
  "ยอดค้าง",
  "รอจัดสรร",
  "เหลือ",
  "ยอด",
  "ยอดโอน",
  "รับแล้ว",
  "จัดสรรแล้ว",
  "สถานะ",
  "ลูกค้า/รายการ",
  "ลูกค้า",
  "ชื่อลูกค้า",
  "ลูกค้า/ผู้รับ",
  "ชื่อผู้ส่ง",
  "ผู้ส่ง",
  "ผู้รับ",
  "โทร",
];

function labelPriority(label: string): number {
  const idx = DIFF_LABEL_PRIORITY.indexOf(label);
  return idx === -1 ? DIFF_LABEL_PRIORITY.length + label.length : idx;
}

/** Parse history lines like "แก้ยอดค้าง 1500 → 2000 บาท" into diffs. */
export function diffsFromHistoryDescription(
  description: string | undefined | null,
): ReceivableStaleFieldDiff[] {
  const text = (description || "").trim();
  if (!text) return [];

  const amountMatch = text.match(
    /แก้ยอดค้าง\s+(.+?)\s*[→➞]\s*(.+?)\s*บาท/,
  );
  if (amountMatch) {
    return [
      {
        label: "ยอดค้าง",
        localValue: formatBahtDisplay(amountMatch[1]),
        remoteValue: formatBahtDisplay(amountMatch[2]),
      },
    ];
  }

  const nameMatch = text.match(/แก้ชื่อลูกค้า\s+(.+?)\s*[→➞]\s*(.+)$/);
  if (nameMatch) {
    return [
      {
        label: "ชื่อ",
        localValue: nameMatch[1].trim() || "—",
        remoteValue: nameMatch[2].trim() || "—",
      },
    ];
  }

  const senderMatch = text.match(
    /แก้(?:ชื่อ)?ผู้โอน\s+(.+?)\s*[→➞]\s*(.+)$/,
  );
  if (senderMatch) {
    return [
      {
        label: "ผู้โอน",
        localValue: senderMatch[1].trim() || "—",
        remoteValue: senderMatch[2].trim() || "—",
      },
    ];
  }

  const arrowMatch = text.match(/^(.+?)\s+(\S+)\s*[→➞]\s*(\S+)\s*(.*)$/);
  if (arrowMatch) {
    const label = arrowMatch[1].replace(/^แก้/, "").trim() || "รายการ";
    const left = arrowMatch[2];
    const right = arrowMatch[3];
    const leftNum = parseAmountNumber(left);
    const rightNum = parseAmountNumber(right);
    if (leftNum !== null && rightNum !== null) {
      return [
        {
          label,
          localValue: formatBahtDisplay(left),
          remoteValue: formatBahtDisplay(right),
        },
      ];
    }
    return [
      {
        label,
        localValue: left,
        remoteValue: right,
      },
    ];
  }

  return [];
}

function pushAllFieldDiffs(
  diffs: ReceivableStaleFieldDiff[],
  localItem: ReceivableListItem,
  remoteItem: ReceivableListItem,
) {
  const remoteByKey = new Map(
    remoteItem.fields.map((field) => [field.key, field] as const),
  );
  const seen = new Set<string>();

  for (const localField of localItem.fields) {
    if (FIELD_DIFF_SKIP_KEYS.has(localField.key)) continue;
    seen.add(localField.key);
    const remoteField = remoteByKey.get(localField.key);
    const localValue = (localField.value || "").trim();
    const remoteValue = (remoteField?.value || "").trim();
    if (!localValue && !remoteValue) continue;
    if (amountsEqual(localValue || "—", remoteValue || "—")) continue;
    const label = (localField.label || remoteField?.label || localField.key).trim();
    const looksAmount =
      /amount|ยอด|บาท|paid|remaining|original|allocated/i.test(localField.key) ||
      /ยอด|บาท|รับแล้ว|รอจัดสรร|เหลือ/.test(label);
    if (looksAmount) {
      pushAmountDiff(diffs, label, localValue, remoteValue);
    } else {
      pushTextDiff(diffs, label, localValue, remoteValue);
    }
  }

  for (const remoteField of remoteItem.fields) {
    if (FIELD_DIFF_SKIP_KEYS.has(remoteField.key) || seen.has(remoteField.key)) {
      continue;
    }
    const remoteValue = (remoteField.value || "").trim();
    if (!remoteValue) continue;
    const label = (remoteField.label || remoteField.key).trim();
    const looksAmount =
      /amount|ยอด|บาท|paid|remaining|original|allocated/i.test(remoteField.key) ||
      /ยอด|บาท|รับแล้ว|รอจัดสรร|เหลือ/.test(label);
    if (looksAmount) {
      pushAmountDiff(diffs, label, "", remoteValue);
    } else {
      pushTextDiff(diffs, label, "", remoteValue);
    }
  }
}

function preferLabelForAction(
  actionTitle: string | undefined,
  labels: string[],
): string {
  const action = (actionTitle || "").trim();
  const pick = (...candidates: string[]) =>
    candidates.find((c) => labels.includes(c));

  if (/ผู้โอน|sender/i.test(action)) {
    return pick("ผู้โอน", "ชื่อผู้ส่ง", "ผู้ส่ง") || labels[0]!;
  }
  if (/ชื่อ|ลูกค้า|customer/i.test(action)) {
    return pick("ชื่อ", "ลูกค้า/รายการ", "ลูกค้า", "ชื่อลูกค้า") || labels[0]!;
  }
  if (/ยอด|amount/i.test(action)) {
    return (
      pick("ยอดเต็ม", "ยอดค้าง", "รอจัดสรร", "เหลือ", "ยอด", "ยอดโอน") || labels[0]!
    );
  }
  if (/ที่อยู่|address/i.test(action)) {
    return pick("ที่อยู่") || labels[0]!;
  }

  return [...labels].sort((a, b) => labelPriority(a) - labelPriority(b))[0]!;
}

/**
 * Collapse rows that show the exact same before → after under synonym labels
 * (e.g. ชื่อ + ลูกค้า/รายการ, or ยอดเต็ม + ยอดค้าง + เหลือ on unpaid edit).
 */
function collapseRedundantDiffs(
  diffs: ReceivableStaleFieldDiff[],
  actionTitle?: string,
): ReceivableStaleFieldDiff[] {
  if (diffs.length <= 1) return diffs;

  const byTransition = new Map<string, ReceivableStaleFieldDiff[]>();
  for (const row of diffs) {
    const key = `${row.localValue}\0${row.remoteValue}`;
    const group = byTransition.get(key);
    if (group) group.push(row);
    else byTransition.set(key, [row]);
  }

  const collapsed: ReceivableStaleFieldDiff[] = [];
  for (const group of byTransition.values()) {
    if (group.length === 1) {
      collapsed.push(group[0]!);
      continue;
    }
    const labels = group.map((g) => g.label);
    const preferred = preferLabelForAction(actionTitle, labels);
    const chosen =
      group.find((g) => g.label === preferred) ||
      [...group].sort(
        (a, b) => labelPriority(a.label) - labelPriority(b.label),
      )[0]!;
    collapsed.push({ ...chosen, label: preferred });
  }

  collapsed.sort((a, b) => labelPriority(a.label) - labelPriority(b.label));
  return collapsed;
}

export function buildReceivableStaleConflict(
  localItem: ReceivableListItem,
  remoteItem: ReceivableListItem,
  notice: ReceivableStaleNotice,
): ReceivableStaleConflict {
  const diffs: ReceivableStaleFieldDiff[] = [];

  pushTextDiff(
    diffs,
    "ชื่อ",
    pickDisplayName(localItem),
    pickDisplayName(remoteItem),
  );
  pushTextDiff(
    diffs,
    "ผู้โอน",
    pickSlipSender(localItem),
    pickSlipSender(remoteItem),
  );
  pushTextDiff(
    diffs,
    "ที่อยู่",
    pickAddressText(localItem),
    pickAddressText(remoteItem),
  );

  pushAmountDiff(
    diffs,
    "ยอดเต็ม",
    pickAmountOriginal(localItem),
    pickAmountOriginal(remoteItem),
  );
  pushAmountDiff(
    diffs,
    "ยอดค้าง",
    pickAmountRemaining(localItem),
    pickAmountRemaining(remoteItem),
  );
  pushAmountDiff(
    diffs,
    "รับแล้ว",
    pickAmountPaid(localItem),
    pickAmountPaid(remoteItem),
  );

  const hasAmountDiff = diffs.some((d) =>
    ["ยอดเต็ม", "ยอดค้าง", "รับแล้ว"].includes(d.label),
  );
  if (!hasAmountDiff) {
    pushAmountDiff(
      diffs,
      "ยอด",
      localItem.amountLabel?.trim() || pickAmountRemaining(localItem),
      remoteItem.amountLabel?.trim() || pickAmountRemaining(remoteItem),
    );
  }

  const localStatuses = pickStatuses(localItem);
  const remoteStatuses = pickStatuses(remoteItem);
  if (localStatuses !== remoteStatuses) {
    diffs.push({
      label: "สถานะ",
      localValue: localStatuses || "—",
      remoteValue: remoteStatuses || "—",
    });
  }

  // Catch any other field the structured picks missed (name keys, etc.).
  pushAllFieldDiffs(diffs, localItem, remoteItem);

  // History description is authoritative for edit amount/name (before → after).
  for (const row of diffsFromHistoryDescription(notice.changeDescription)) {
    if (!diffs.some((d) => d.label === row.label)) {
      diffs.push(row);
    }
  }

  const collapsed = collapseRedundantDiffs(diffs, notice.actionTitle);

  if (collapsed.length === 0) {
    collapsed.push({
      label: "รายการ",
      localValue: "ข้อมูลก่อนแก้",
      remoteValue: "ข้อมูลหลังแก้",
    });
  }

  return { notice, localItem, remoteItem, diffs: collapsed };
}

export function fingerprintReceivableItem(item: ReceivableListItem): string {
  const updatedAt = pickField(item, "updated_at");
  const original = pickAmountOriginal(item);
  const remaining =
    pickAmountRemaining(item) || item.amountLabel?.trim() || "";
  const paid = pickAmountPaid(item);
  const name = pickDisplayName(item);
  const address = pickAddressText(item);
  const statuses = [...item.statuses].sort().join(",");
  return [
    item.mergeKey,
    updatedAt,
    original,
    remaining,
    paid,
    name,
    address,
    statuses,
  ].join("|");
}

function formatOccurredAtLabel(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "เมื่อสักครู่";
  if (/[ก-๙]/.test(trimmed) || trimmed.includes("เมื่อ")) return trimmed;
  const formatted = formatThaiDatetime(trimmed);
  return formatted || trimmed || "เมื่อสักครู่";
}

export function detectStaleReceivableEdit(
  baseline: string,
  next: ReceivableListItem,
  currentOwnerUserId?: string | null,
): ReceivableStaleNotice | null {
  const nextFingerprint = fingerprintReceivableItem(next);
  if (nextFingerprint === baseline) return null;

  const updatedAt = pickField(next, "updated_at");
  const rawActor =
    pickField(next, "updated_by_name") ||
    pickField(next, "updated_by") ||
    pickField(next, "last_actor_name") ||
    "";
  const owner = (currentOwnerUserId || "").trim();
  const isSelf = Boolean(owner && rawActor && rawActor === owner);
  const actorName = isSelf
    ? "คุณ"
    : formatHistoryActorLabel(rawActor) || "ผู้ใช้อื่น";

  return {
    actorName,
    actorId: rawActor || undefined,
    summary: "มีการแก้ไขรายการนี้ขณะที่คุณกำลังดูอยู่",
    occurredAt: formatOccurredAtLabel(updatedAt),
  };
}

export function enrichStaleNoticeFromHistory(
  base: ReceivableStaleNotice,
  entry: ReceivableHistoryEntry | null | undefined,
  currentOwnerUserId?: string | null,
): ReceivableStaleNotice {
  if (!entry) return base;

  const actorId = (entry.actorId || "").trim();
  const owner = (currentOwnerUserId || "").trim();
  const formatted = formatHistoryActorLabel(entry.actorName || actorId);
  const isSelf = Boolean(owner && actorId && actorId === owner);

  return {
    actorName: isSelf ? "คุณ" : formatted || base.actorName || "ผู้ใช้อื่น",
    actorId: actorId || base.actorId,
    actionTitle: entry.title?.trim() || base.actionTitle,
    summary: entry.title?.trim()
      ? `ทำรายการ「${entry.title.trim()}」ขณะที่คุณกำลังดูอยู่`
      : base.summary,
    occurredAt: entry.occurredAt?.trim() || base.occurredAt,
    changeDescription:
      entry.description?.trim() || base.changeDescription || undefined,
  };
}

const OWN_EDIT_STORAGE_KEY = "aera-receivable-own-edit-tab";

type OwnEditMarker = {
  mergeKey: string;
  at: number;
};

/** Mark that THIS browser tab just edited the open row (sessionStorage is per-tab). */
export function markOwnReceivableEditInThisTab(mergeKey: string): void {
  const key = mergeKey.trim();
  if (!key || typeof window === "undefined") return;
  try {
    const marker: OwnEditMarker = { mergeKey: key, at: Date.now() };
    window.sessionStorage.setItem(OWN_EDIT_STORAGE_KEY, JSON.stringify(marker));
  } catch {
    // ignore storage errors
  }
}

/** True when this tab recently edited the row — other tabs will not see this marker. */
export function wasOwnReceivableEditInThisTab(
  mergeKey: string,
  withinMs = 30_000,
): boolean {
  const key = mergeKey.trim();
  if (!key || typeof window === "undefined") return false;
  try {
    const raw = window.sessionStorage.getItem(OWN_EDIT_STORAGE_KEY);
    if (!raw) return false;
    const marker = JSON.parse(raw) as OwnEditMarker;
    if (!marker || marker.mergeKey !== key) return false;
    if (Date.now() - Number(marker.at || 0) > withinMs) return false;
    return true;
  } catch {
    return false;
  }
}
